// Core React and Router imports
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Context Providers
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthProvider';
import { TeamsProvider } from './contexts/TeamsProvider';
import { UserProvider } from './contexts/UserContext';

// Core Components
import { Toaster } from './components/ui/toast';
import Layout from './components/Layout';
import ProtectedRoute from './components/auth/protectedroute';
import OnboardingRedirect from './components/auth/OnboardingRedirect';
import OnboardingGuard from './components/auth/OnboardingGuard';
import { FeatureProtectedRoute } from './components/auth/FeatureProtectedRoute';

// Feature Preview Components
import { DomainsFeaturePreview } from './components/features/DomainsFeaturePreview';
import { SubdomainsFeaturePreview } from './components/features/SubdomainsFeaturePreview';
import { IpAddressesFeaturePreview } from './components/features/IpAddressesFeaturePreview';
import { PortsFeaturePreview } from './components/features/PortsFeaturePreview';
import { WebApplicationsFeaturePreview } from './components/features/WebApplicationsFeaturePreview';
import { SecurityFindingsFeaturePreview } from './components/features/SecurityFindingsFeaturePreview';
import { SecurityCenterFeaturePreview } from './components/features/SecurityCenterFeaturePreview';
import { BrandProtectionFeaturePreview } from './components/features/BrandProtectionFeaturePreview';
import { DomainMonitoringFeaturePreview } from './components/features/DomainMonitoringFeaturePreview';
import { InfringementsFeaturePreview } from './components/features/InfringementsFeaturePreview';
import { DataProtectionFeaturePreview } from './components/features/DataProtectionFeaturePreview';
import { PublicLeaksFeaturePreview } from './components/features/PublicLeaksFeaturePreview';
import { DataLeaksFeaturePreview } from './components/features/DataLeaksFeaturePreview';
import { StealerLeaksFeaturePreview } from './components/features/StealerLeaksFeaturePreview';
import { VipProfilesFeaturePreview } from './components/features/VipProfilesFeaturePreview';
import { ComplianceFeaturePreview } from './components/features/ComplianceFeaturePreview';
import { NacsaChecklistFeaturePreview } from './components/features/NacsaChecklistFeaturePreview';
import { ReportsFeaturePreview } from './components/features/ReportsFeaturePreview';

// Error Pages
import NotFound from './pages/error/404';
import ServerError from './pages/error/500';

// Authentication Pages
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import SignOut from './pages/auth/SignOut';
import ForgotPassword from './pages/auth/ForgotPassword';
import ConfirmPasswordReset from './pages/auth/ConfirmPasswordReset';
import TwoFactorVerification from './pages/auth/TwoFactorVerification';
import ResendVerificationEmail from './pages/auth/ResendVerificationEmail';
import VerifyEmail from './pages/auth/VerifyEmail';
import MagicLinkRequest from './pages/auth/MagicLinkRequest';
import MagicLinkLogin from './pages/auth/MagicLinkLogin';

// Core Pages
import Dashboard from './pages/Dashboard';
import AttackSurfaceGraph from './pages/AttackSurfaceGraph';
import Discovery from './pages/Discovery';
import Monitoring from './pages/Monitoring';
import Reports from './pages/Reports';
import ApiDocs from './pages/ApiDocs';
// import ScanPage from './pages/ScanPage';

// Asset Management
import AllAssets from './pages/AllAssets';
import WebApplications from './pages/WebApplications';
import Domains from './pages/assets/Domains';
import Subdomains from './pages/assets/Subdomains';
import IpAddresses from './pages/assets/IpAddresses';
import Ports from './pages/assets/Ports';
import PortDetail from './pages/assets/PortDetail';
import AssetDetail from './pages/assets/AssetDetail';
import DomainDetail from './pages/assets/DomainDetail';
import SubdomainDetail from './pages/assets/SubdomainDetail';
import IpDetails from './pages/assets/IpDetails';

// VIP Management
import VIPProfiles from './pages/VIPProfiles';
import VIPProfileDetail from './pages/VIPProfileDetail';

// Findings and Incidents
import Findings from './pages/Findings';
import FindingDetail from './pages/findings/FindingDetail';
import Incidents from './pages/Incidents';
import IncidentDetails from './pages/incidents/IncidentDetails';

// Brand Protection
import BrandDashboard from './pages/brand-protection/BrandDashboard';
import BrandImpersonation from './pages/brand-protection/BrandImpersonation';
import ViolationDetails from './pages/brand-protection/ViolationDetails';
import InfringementDetails from './pages/brand-protection/InfringementDetails';
import DomainMonitoring from './pages/brand-protection/DomainMonitoring';
import InfringementsList from './pages/brand-protection/InfringementsList';
import MarketplaceMonitoring from './pages/brand-protection/MarketplaceMonitoring';
import SocialMediaMonitoring from './pages/brand-protection/SocialMediaMonitoring';
import TrademarkMonitoring from './pages/brand-protection/TrademarkMonitoring';
import DomainThreatDetails from './pages/brand-protection/DomainThreatDetails';

// Data Protection
import CredentialLeaks from './pages/data-protection/CredentialLeaks';
import DataLeaks from './pages/data-protection/DataLeaks';
import LeaksDashboard from './pages/data-protection/LeaksDashboard';

import PublicLeaksList from './pages/data-protection/PublicLeaksList';
import DataLeaksList from './pages/data-protection/DataLeaksList';
import DataLeakDetail from './pages/data-protection/DataLeakDetail';
import SecurityAlerts from './pages/data-protection/SecurityAlerts';
import StealerLeaksList from './pages/data-protection/StealerLeaksList';

// Security Features
import DomainSecurity from './pages/DomainSecurity';
import DomainSecurityDetail from './pages/DomainSecurityDetail';
import EmailSecurity from './pages/EmailSecurity';
import EmailSecurityDetail from './pages/EmailSecurityDetail';
import SecurityCenter from './pages/SecurityCenter';
import ScanSettings from './pages/scan-settings/ScanSettings';
import SecurityCenterDetail from './pages/SecurityCenterDetail';

// Threat Intelligence
import ThreatDashboard from './pages/threat-intelligence/ThreatDashboard';
import ThreatNews from './pages/threat-intelligence/ThreatNews';
import ThreatFeeds from './pages/threat-intelligence/ThreatFeeds';
import ThreatFeedsList from './pages/threat-intelligence/ThreatFeedsList';
import CVEDashboard from './pages/threat-intelligence/CVEDashboard';
import CVEList from './pages/threat-intelligence/CVEList';

// Compliance
import Compliance from './pages/Compliance';
import NacsaChecklist from './pages/compliance/ComplianceChecklist';

// Settings
import Settings from './pages/Settings';
import { GeneralSettings } from './components/settings/GeneralSettings';
import { SecuritySettings } from './components/settings/SecuritySettings';
import { NotificationSettingsPage } from './components/settings/NotificationSettings';
import { APISettings } from './components/settings/APISettings';
import { IntegrationSettings } from './components/settings/IntegrationSettings';
import { TeamSettings } from './components/settings/TeamSettings';
import TeamSwitcher from './pages/TeamSwitcher';
import NotificationDetail from './pages/NotificationDetail';
import GenericNotificationDetail from './pages/GenericNotificationDetail';
import AssetNotificationDetail from './pages/AssetNotificationDetail';
import AllNotifications from './pages/AllNotifications';

// Onboarding
import { OnboardingWizard } from './components/onboarding/OnboardingWizard';
import InvitationLanding from './pages/InvitationLanding';
import SignupAfterInvitation from './pages/invitations/SignupInvitation';
import RiskDetail from './pages/vip/RiskDetail';
import StealerLeakDetail from './pages/data-protection/StealerLeakDetail';
import PublicLeakDetail from './pages/data-protection/PublicLeakDetail';
import CredentialLeakSearch from './pages/data-protection/CredentialLeakSearch';
import SecurityCenterV2 from './pages/SecurityCenterV2';
import { AlertsSettingsPage } from './components/settings/AlertsSettings';

const App = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <UserProvider>
          <TeamsProvider>
            <Router>
              <Toaster />
              <Routes>
                {/* Auth Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/signout" element={<SignOut />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ConfirmPasswordReset />} />
                <Route path="/2fa" element={<TwoFactorVerification />} />

                <Route path="/invitation/:invitationId" element={<InvitationLanding />} />
                <Route path="/invitation/signup/:invitationId" element={<SignupAfterInvitation />} />

                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/resend-verification" element={<ResendVerificationEmail />} />
                <Route path="/magic-link" element={<MagicLinkRequest />} />
                <Route path="/magic-link/login" element={<MagicLinkLogin />} />
                <Route path="/onboarding" element={
                  <OnboardingGuard>
                    <OnboardingWizard />
                  </OnboardingGuard>
                } />
            
                {/* Protected Routes */}
                <Route path="/*" element={
                  <ProtectedRoute>
                    <OnboardingRedirect>
                      <Layout>
                        <Routes>
                          <Route index element={<Dashboard />} />
                          <Route path="attack-surface" element={<AttackSurfaceGraph />} />
                    
                    <Route path="discovery" element={<Discovery />} />
                    {/* <Route path="scan" element={<ScanPage />} /> */}
                    {/* Notification Routes */}
                    {/* Notification Routes */}
                    <Route path="notifications">
                      {/* <Route path="finding/:id" element={<NotificationDetail />} /> */}
                      <Route path="asset/:id" element={<AssetNotificationDetail />} />
                      <Route path=":type/:id" element={<GenericNotificationDetail />} />
                      <Route index element={<AllNotifications />} />
                    </Route>
                    <Route path="assets" element={<AllAssets />} />
                    <Route path="web-applications" element={
                      <FeatureProtectedRoute 
                        featureKey="hasWebApplicationsFeature"
                        fallbackComponent={<WebApplicationsFeaturePreview />}
                      >
                        <WebApplications />
                      </FeatureProtectedRoute>
                    } />
                    <Route path="domains" element={
                      <FeatureProtectedRoute 
                        featureKey="hasDomainsFeature"
                        fallbackComponent={<DomainsFeaturePreview />}
                      >
                        <Domains />
                      </FeatureProtectedRoute>
                    } />
                    <Route path="domains/:id" element={
                      <FeatureProtectedRoute 
                        featureKey="hasDomainsFeature"
                        fallbackComponent={<DomainsFeaturePreview />}
                      >
                        <DomainDetail />
                      </FeatureProtectedRoute>
                    } />
                    <Route path="subdomains" element={
                      <FeatureProtectedRoute 
                        featureKey="hasSubdomainsFeature"
                        fallbackComponent={<SubdomainsFeaturePreview />}
                      >
                        <Subdomains />
                      </FeatureProtectedRoute>
                    } />
                    <Route path="subdomains/:id" element={
                      <FeatureProtectedRoute 
                        featureKey="hasSubdomainsFeature"
                        fallbackComponent={<SubdomainsFeaturePreview />}
                      >
                        <SubdomainDetail />
                      </FeatureProtectedRoute>
                    } />
                    <Route path="ip-addresses" element={
                      <FeatureProtectedRoute 
                        featureKey="hasIpAddressesFeature"
                        fallbackComponent={<IpAddressesFeaturePreview />}
                      >
                        <IpAddresses />
                      </FeatureProtectedRoute>
                    } />
                    <Route path="ip-addresses/:id" element={
                      <FeatureProtectedRoute 
                        featureKey="hasIpAddressesFeature"
                        fallbackComponent={<IpAddressesFeaturePreview />}
                      >
                        <IpDetails />
                      </FeatureProtectedRoute>
                    } />

                    <Route path="ports" element={
                      <FeatureProtectedRoute 
                        featureKey="hasPortsFeature"
                        fallbackComponent={<PortsFeaturePreview />}
                      >
                        <Ports />
                      </FeatureProtectedRoute>
                    } />

                    {/* Brand Protection Routes */}
                    <Route path="brand-protection">
                      <Route index element={
                        <FeatureProtectedRoute 
                          featureKey="hasDomainMonitoringFeature"
                          fallbackComponent={<BrandProtectionFeaturePreview />}
                        >
                          <BrandDashboard />
                        </FeatureProtectedRoute>
                      } />
                      <Route path="impersonation" element={
                        <FeatureProtectedRoute 
                          featureKey="hasDomainMonitoringFeature"
                          fallbackComponent={<BrandProtectionFeaturePreview />}
                        >
                          <BrandImpersonation />
                        </FeatureProtectedRoute>
                      } />
                      <Route path="domain-monitoring" element={
                        <FeatureProtectedRoute 
                          featureKey="hasDomainMonitoringFeature"
                          fallbackComponent={<DomainMonitoringFeaturePreview />}
                        >
                          <DomainMonitoring />
                        </FeatureProtectedRoute>
                      } />
                      <Route path="infringements" element={
                        <FeatureProtectedRoute 
                          featureKey="hasInfringementsFeature"
                          fallbackComponent={<InfringementsFeaturePreview />}
                        >
                          <InfringementsList />
                        </FeatureProtectedRoute>
                      } />
                      <Route path="infringements/:id" element={
                        <FeatureProtectedRoute 
                          featureKey="hasInfringementsFeature"
                          fallbackComponent={<InfringementsFeaturePreview />}
                        >
                          <InfringementDetails />
                        </FeatureProtectedRoute>
                      } />
                      <Route path="threats/:id" element={
                        <FeatureProtectedRoute 
                          featureKey="hasDomainMonitoringFeature"
                          fallbackComponent={<DomainMonitoringFeaturePreview />}
                        >
                          <DomainThreatDetails />
                        </FeatureProtectedRoute>
                      } />
                      <Route path="violations/:id" element={
                        <FeatureProtectedRoute 
                          featureKey="hasInfringementsFeature"
                          fallbackComponent={<InfringementsFeaturePreview />}
                        >
                          <ViolationDetails />
                        </FeatureProtectedRoute>
                      } />
                      <Route path="marketplace" element={
                        <FeatureProtectedRoute 
                          featureKey="hasInfringementsFeature"
                          fallbackComponent={<InfringementsFeaturePreview />}
                        >
                          <MarketplaceMonitoring />
                        </FeatureProtectedRoute>
                      } />
                      <Route path="social-media" element={
                        <FeatureProtectedRoute 
                          featureKey="hasInfringementsFeature"
                          fallbackComponent={<InfringementsFeaturePreview />}
                        >
                          <SocialMediaMonitoring />
                        </FeatureProtectedRoute>
                      } />
                      <Route path="trademark" element={
                        <FeatureProtectedRoute 
                          featureKey="hasInfringementsFeature"
                          fallbackComponent={<InfringementsFeaturePreview />}
                        >
                          <TrademarkMonitoring />
                        </FeatureProtectedRoute>
                      } />
                    </Route>

                    {/* Compliance Routes */}
                    <Route path="compliance">
                      <Route index element={
                        <FeatureProtectedRoute 
                          featureKey="hasNacsaChecklistFeature"
                          fallbackComponent={<ComplianceFeaturePreview />}
                        >
                          <Compliance />
                        </FeatureProtectedRoute>
                      } />
                      <Route path="checklist" element={
                        <FeatureProtectedRoute 
                          featureKey="hasNacsaChecklistFeature"
                          fallbackComponent={<NacsaChecklistFeaturePreview />}
                        >
                          <NacsaChecklist />
                        </FeatureProtectedRoute>
                      } />
                    </Route>

                    {/* Data Protection Routes */}
                    <Route path="data-protection">
                      <Route index element={
                        <FeatureProtectedRoute 
                          featureKey="hasDataOverviewFeature"
                          fallbackComponent={<DataProtectionFeaturePreview />}
                        >
                          <LeaksDashboard />
                        </FeatureProtectedRoute>
                      } />
                      <Route path="public-leaks" element={
                        <FeatureProtectedRoute 
                          featureKey="hasPublicLeaksFeature"
                          fallbackComponent={<PublicLeaksFeaturePreview />}
                        >
                          <PublicLeaksList />
                        </FeatureProtectedRoute>
                      } />
                      <Route path="data-leaks" element={
                        <FeatureProtectedRoute 
                          featureKey="hasDataLeaksFeature"
                          fallbackComponent={<DataLeaksFeaturePreview />}
                        >
                          <DataLeaksList />
                        </FeatureProtectedRoute>
                      } />
                      <Route path="data-leaks/:id" element={
                        <FeatureProtectedRoute 
                          featureKey="hasDataLeaksFeature"
                          fallbackComponent={<DataLeaksFeaturePreview />}
                        >
                          <DataLeakDetail />
                        </FeatureProtectedRoute>
                      } />
                      <Route path="stealer-leaks" element={
                        <FeatureProtectedRoute 
                          featureKey="hasStealerLeaksFeature"
                          fallbackComponent={<StealerLeaksFeaturePreview />}
                        >
                          <StealerLeaksList />
                        </FeatureProtectedRoute>
                      } />
                      <Route path="public-leaks/:id" element={
                        <FeatureProtectedRoute 
                          featureKey="hasPublicLeaksFeature"
                          fallbackComponent={<PublicLeaksFeaturePreview />}
                        >
                          <PublicLeakDetail />
                        </FeatureProtectedRoute>
                      } />
                      <Route path="stealer-leaks/:id" element={
                        <FeatureProtectedRoute 
                          featureKey="hasStealerLeaksFeature"
                          fallbackComponent={<StealerLeaksFeaturePreview />}
                        >
                          <StealerLeakDetail />
                        </FeatureProtectedRoute>
                      } />
                      <Route path="security-alerts" element={
                        <FeatureProtectedRoute 
                          featureKey="hasDataOverviewFeature"
                          fallbackComponent={<DataProtectionFeaturePreview />}
                        >
                          <SecurityAlerts />
                        </FeatureProtectedRoute>
                      } />
                      <Route path="search" element={
                        <FeatureProtectedRoute 
                          featureKey="hasDataSearchFeature"
                          fallbackComponent={<DataProtectionFeaturePreview />}
                        >
                          <CredentialLeakSearch />
                        </FeatureProtectedRoute>
                      } />
                    </Route>

                    {/* Threat Intelligence Routes */}
                    <Route path="threat-intelligence">
                      <Route index element={<ThreatDashboard />} />
                      <Route path="cve" element={<CVEList />} />
                      <Route path="cve-dashboard" element={<CVEDashboard />} />
                      <Route path="threat-feeds-dashboard" element={<ThreatFeeds />} />
                      <Route path="threat-feeds" element={<ThreatFeedsList />} />
                      <Route path="news" element={<ThreatNews />} />
                    </Route>

                    <Route path="ports/:id" element={
                      <FeatureProtectedRoute 
                        featureKey="hasPortsFeature"
                        fallbackComponent={<PortsFeaturePreview />}
                      >
                        <PortDetail />
                      </FeatureProtectedRoute>
                    } />

                    <Route path="assets/:id" element={<AssetDetail />} />

                    <Route path="monitoring" element={<Monitoring />} />
                    <Route path="vip-profiles" element={
                      <FeatureProtectedRoute 
                        featureKey="hasVipProfilesFeature"
                        fallbackComponent={<VipProfilesFeaturePreview />}
                      >
                        <VIPProfiles />
                      </FeatureProtectedRoute>
                    } />
                    <Route path="vip-profiles/:id" element={
                      <FeatureProtectedRoute 
                        featureKey="hasVipProfilesFeature"
                        fallbackComponent={<VipProfilesFeaturePreview />}
                      >
                        <VIPProfileDetail />
                      </FeatureProtectedRoute>
                    } />
                    <Route path="vip-risk/:id" element={
                      <FeatureProtectedRoute 
                        featureKey="hasVipProfilesFeature"
                        fallbackComponent={<VipProfilesFeaturePreview />}
                      >
                        <RiskDetail />
                      </FeatureProtectedRoute>
                    } />
                    <Route path="findings" element={
                      <FeatureProtectedRoute 
                        featureKey="hasSecurityFindingsFeature"
                        fallbackComponent={<SecurityFindingsFeaturePreview />}
                      >
                        <Findings />
                      </FeatureProtectedRoute>
                    } />
                    <Route path="findings/:id" element={
                      <FeatureProtectedRoute 
                        featureKey="hasSecurityFindingsFeature"
                        fallbackComponent={<SecurityFindingsFeaturePreview />}
                      >
                        <FindingDetail />
                      </FeatureProtectedRoute>
                    } />

                    {/* <Route path="domain-security" element={<DomainSecurity />} />
                    <Route path="domain-security/:domain" element={<DomainSecurityDetail />} />
                    <Route path="email-security" element={<EmailSecurity />} />
                    <Route path="email-security/:domain" element={<EmailSecurityDetail />} /> */}
                    <Route path="security-center" element={
                      <FeatureProtectedRoute 
                        featureKey="hasSecurityCenterFeature"
                        fallbackComponent={<SecurityCenterFeaturePreview />}
                      >
                        <SecurityCenterV2 />
                      </FeatureProtectedRoute>
                    } />
                    <Route path="security-center/:type/:domain" element={
                      <FeatureProtectedRoute 
                        featureKey="hasSecurityCenterFeature"
                        fallbackComponent={<SecurityCenterFeaturePreview />}
                      >
                        <SecurityCenterDetail />
                      </FeatureProtectedRoute>
                    } />
                    <Route path="incidents" element={<Incidents />} />
                    <Route path="incidents/:id" element={<IncidentDetails />} />

                    <Route path="reports" element={
                      <FeatureProtectedRoute 
                        featureKey="hasReportsFeature"
                        fallbackComponent={<ReportsFeaturePreview />}
                      >
                        <Reports />
                      </FeatureProtectedRoute>
                    } />
                    <Route path="api-docs" element={<ApiDocs />} />
                    <Route path="scan-settings" element={<ScanSettings />} />

                    <Route path="settings" element={<Settings />}>
                      <Route index element={<GeneralSettings />} />
                      <Route path="team-switcher" element={<TeamSwitcher />}/>
                      <Route path="general" element={<GeneralSettings />} />
                      <Route path="security" element={<SecuritySettings />} />
                      <Route path="notifications" element={<NotificationSettingsPage />} />
                      <Route path="alerts" element={<AlertsSettingsPage />} />
                      <Route path="api" element={<APISettings />} />
                      <Route path="integrations" element={<IntegrationSettings />} />
                      <Route path="team" element={<TeamSettings />} />
                    </Route>

                    <Route path="team" element={<TeamSettings />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Layout>
                </OnboardingRedirect>
              </ProtectedRoute>
            } />
          </Routes>
          </Router>
          </TeamsProvider>
        </UserProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;